import { useEffect, useRef } from "react"
import { useDrawingArea, useXScale } from "@mui/x-charts"

import { useHoverSync } from "helpers/hooks/useHoverSync"
import type { HoverState } from "helpers/contexts/HoverSyncContext"

interface CrosshairLineProps {
  xDomainMin: number
  xDomainMax: number
}

/**
 * Renders vertical + horizontal crosshair lines inside the SVG of a LineChart or ScatterChart.
 * Subscribes to HoverSyncContext and updates SVG <line> elements directly via refs
 * — zero React re-renders on hover.
 *
 * Must be rendered as a direct child of LineChart / ScatterChart.
 */
const CrosshairLine = ({ xDomainMin, xDomainMax }: CrosshairLineProps) => {
  const { subscribe } = useHoverSync()
  const drawingArea = useDrawingArea()
  const xScale = useXScale()
  const vLineRef = useRef<SVGLineElement>(null)
  const hLineRef = useRef<SVGLineElement>(null)

  // Keep a stable ref to the latest scale / drawing area so the subscription
  // callback never has stale values without needing to re-subscribe.
  const stateRef = useRef({ drawingArea, xScale, xDomainMin, xDomainMax })
  stateRef.current = { drawingArea, xScale, xDomainMin, xDomainMax }

  useEffect(() => {
    const hide = () => {
      vLineRef.current?.setAttribute("visibility", "hidden")
      hLineRef.current?.setAttribute("visibility", "hidden")
    }

    const unsubscribe = subscribe((state: HoverState | null) => {
      if (state === null) {
        hide()
        return
      }

      const {
        drawingArea: da,
        xScale: scale,
        xDomainMin: min,
        xDomainMax: max,
      } = stateRef.current

      // — Vertical line (X) —
      let pixelX: number | undefined
      if (scale) {
        const scaled = scale(new Date(state.x))
        if (scaled !== undefined) pixelX = scaled
      }
      if (pixelX === undefined) {
        const range = max - min
        if (range === 0 || !isFinite(range)) return
        pixelX = da.left + ((state.x - min) / range) * da.width
      }

      if (pixelX < da.left || pixelX > da.left + da.width) {
        hide()
        return
      }

      // — Horizontal line (Y from proportion) —
      const pixelY = da.top + state.yProportion * da.height

      const vLine = vLineRef.current
      if (vLine) {
        vLine.setAttribute("x1", String(pixelX))
        vLine.setAttribute("x2", String(pixelX))
        vLine.setAttribute("y1", String(da.top))
        vLine.setAttribute("y2", String(da.top + da.height))
        vLine.setAttribute("visibility", "visible")
      }

      const hLine = hLineRef.current
      if (hLine) {
        hLine.setAttribute("x1", String(da.left))
        hLine.setAttribute("x2", String(da.left + da.width))
        hLine.setAttribute("y1", String(pixelY))
        hLine.setAttribute("y2", String(pixelY))
        hLine.setAttribute("visibility", "visible")
      }
    })

    return unsubscribe
  }, [subscribe])

  const lineProps = {
    visibility: "hidden" as const,
    stroke: "currentColor",
    strokeOpacity: 0.2,
    strokeWidth: 1,
    strokeDasharray: "4 4",
    pointerEvents: "none" as const,
  }

  return (
    <>
      <line ref={vLineRef} {...lineProps} />
      <line ref={hLineRef} {...lineProps} />
    </>
  )
}

export default CrosshairLine
